import { useFormik, FormikProvider } from "formik";
import { IPartner, emptyPartners } from "../../../../types/partners.type";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import InputFormik from "../../../../common/components/form/InputFormik";
import * as Yup from "yup";
import { useContext, useEffect, useMemo, useState } from "react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import {
  maskCNPJ,
  maskCurrency,
  maskPhone,
} from "../../../../common/helpers/mask.helpers";
import AddressFields from "../../../renters/components/forms/fields/AddressFields";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { AuthContext } from "../../../../common/components/auth/authContext/AuthContext";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";

type FormPropsCreatePartners = {
  onSubmit: (
    values: IPartner
  ) => Promise<{ ok: boolean; data: IPartner; errors?: unknown[] }>;
  data: IPartner;
  formRequestErrors?: { message: string; path: string[] }[];
};

const validationSchema = Yup.object({
  companyName: Yup.string().required("Nome fantasia é obrigatório."),
  fantasyName: Yup.string().required("Nome fantasia é obrigatório."),
  cnpj: Yup.string().required("cnpj é obrigatório."),
  owner: Yup.object().shape({
    name: Yup.string().required("Nome do proprietário é obrigatório."),
    phone: Yup.string().required("WhatsApp é obrigatório."),
  }),
  operationalContact: Yup.object().shape({
    name: Yup.string().required("Nome do contato operacional é obrigatório."),
    phone: Yup.string().required("WhatsApp é obrigatório."),
  }),
  email: Yup.string().email("E-mail inválido").required("E-mail é obrigatório"),
  companyType: Yup.string().required("Tipo de empresa é obrigatório."),
});

function FormEditPartnersData(props: FormPropsCreatePartners) {
  const { notifySuccess } = useToastContext();
  const [isCreating, setIsCreating] = useState(false);
  const { auth } = useContext(AuthContext);
  const authCtx = useMemo(() => auth, [auth]);
  const permissions = authCtx?.user?.permissions;
  const [isManagementTrafficfines, setIsManagementTrafficfines] =
    useState(false);

  const formik = useFormik({
    initialValues: isCreating
      ? emptyPartners()
      : {
          ...props.data,
          managementTrafficfines: {
            enabled: props.data?.managementTrafficfines?.enabled || false,
            date: props.data?.managementTrafficfines?.date
              ? new Date(props.data.managementTrafficfines.date)
                  .toISOString()
                  .split("T")[0]
              : "",
          },
          digitalCertificateExpirationDate: props.data
            ?.digitalCertificateExpirationDate
            ? new Date(props.data.digitalCertificateExpirationDate)
                .toISOString()
                .split("T")[0]
            : "",
        },
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (data: IPartner) => {
      const submitData = { ...data };

      if (submitData.digitalCertificateExpirationDate) {
        const dateStr = submitData.digitalCertificateExpirationDate;

        let localDate: Date;

        if (typeof dateStr === "string") {
          const [year, month, day] = dateStr.split("-").map(Number);
          localDate = new Date(year, month - 1, day, 12, 0, 0, 0);
        } else {
          localDate = new Date(dateStr);
          localDate.setHours(12, 0, 0, 0);
        }

        submitData.digitalCertificateExpirationDate = localDate.toISOString();
      }

      const result = await props.onSubmit(submitData);

      if (result.ok) {
        formik.setValues({
          ...result.data,
          managementTrafficfines: {
            ...result.data.managementTrafficfines,
            date: result.data.managementTrafficfines?.date
              ? new Date(result.data.managementTrafficfines.date)
                  .toISOString()
                  .split("T")[0]
              : "",
          },
          digitalCertificateExpirationDate: result.data
            .digitalCertificateExpirationDate
            ? new Date(result.data.digitalCertificateExpirationDate)
                .toISOString()
                .split("T")[0]
            : "",
        });
        notifySuccess("Atualizado com sucesso.");
      }
      return result;
    },
  });

  useEffect(() => {
    setIsCreating(!props.data);
    setIsManagementTrafficfines(
      props.data?.managementTrafficfines.enabled || false
    );
  }, [props.data]);

  return (
    <form onSubmit={formik.handleSubmit}>
      <FormikProvider value={formik}>
        <section className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-semibold mb-4">Informações Pessoais</h2>
          <div className="grid grid-cols-3 gap-2 mb-4">
            <div className="grid grid-cols-1 text-sm lg:text-lg col-span-3 gap-2 sm:grid-cols-2 xl:grid-cols-3">
              <InputFormik
                label="Nome da Empresa:"
                name="companyName"
                className=""
                required={true}
              />
              <InputFormik
                label="Nome Fantasia:"
                name="fantasyName"
                className=""
                required={true}
              />
              <InputFormik
                label="CNPJ:"
                name="cnpj"
                placeholder="11.222.333/0001-00"
                required={true}
                mask={maskCNPJ}
              />
              <InputFormik
                label="Inscrição Estadual:"
                name="stateRegistration"
                placeholder="00.000.000/0"
                required={false}
                mask={maskCNPJ}
              />

              <InputFormik
                label="Nome do Proprietário:"
                name="owner.name"
                className=""
                required={true}
              />
              <InputFormik
                label="WhatsApp do Proprietário:"
                name="owner.phone"
                type="whatsappComercial"
                required={true}
                mask={maskPhone}
              />
              <InputFormik
                label="E-mail:"
                name="email"
                type="email"
                required={true}
              />
              <InputFormik
                label="Nome do Contato Operacional:"
                name="operationalContact.name"
                className=""
                required={true}
              />
              <InputFormik
                label="WhatsApp Operacional:"
                name="operationalContact.phone"
                type="whatsappOperacional"
                required={true}
                mask={maskPhone}
              />

              <ComboBoxFormik
                label="Tipe de empresa:"
                name="companyType"
                options={[
                  { value: "", label: "-" },
                  { value: "mei", label: "MEI" },
                  { value: "limited", label: "Limitada" },
                  { value: "individual", label: "Individual" },
                  { value: "association", label: "Associação" },
                ]}
                required={true}
              />
              <ComboBoxFormik
                label="Tipo de Parceiro:"
                name="partnersType"
                options={
                  permissions?.includes("create:companies")
                    ? [
                        { value: "", label: "-" },
                        { value: "partner_in", label: "Interno" },
                        { value: "partner_out", label: "Externo" },
                      ]
                    : [
                        { value: "", label: "-" },
                        { value: "partner_in", label: "Interno" },
                      ]
                }
                required={true}
              />

              <InputFormik
                label="Faturamento Mensal:"
                name="incomeValue"
                type=""
                required={true}
                mask={maskCurrency}
              />

              <InputFormik
                label="Data de Expiração do Certificado Digital:"
                name="digitalCertificateExpirationDate"
                type="date"
              />
            </div>
          </div>
          <div className="bg-gray2/20 mt-6 mb-6 p-3 rounded-md shadow">
            <h2 className="text-xl font-semibold mb-4">
              Gestão de multas Locavibe
            </h2>
            <div className="mt-3 row grid-cols-1 sm:grid-cols-2 items-center">
              <div className="flex items-center">
                <label
                  htmlFor="managementTrafficfines"
                  className="flex items-center gap-2 text-sm font-medium text-gray-600"
                >
                  {isManagementTrafficfines ? (
                    <div className="flex gap-2 items-center text-greenPrimary">
                      <ImCheckboxChecked />
                      <span>Ativo?</span>
                    </div>
                  ) : (
                    <div className="flex gap-2 items-center">
                      <ImCheckboxUnchecked />
                      <span>Ativo?</span>
                    </div>
                  )}
                </label>
                <input
                  id="managementTrafficfines"
                  name="managementTrafficfines"
                  type="checkbox"
                  checked={isManagementTrafficfines}
                  onChange={() => {
                    const newValue = !isManagementTrafficfines;
                    setIsManagementTrafficfines(newValue);
                    formik.setFieldValue(
                      "managementTrafficfines.enabled",
                      newValue
                    );
                  }}
                  className="absolute opacity-0"
                />
              </div>
              {isManagementTrafficfines && (
                <div className="flex items-center mt-3">
                  <InputFormik
                    label="Data de adesão:"
                    className="text-xl"
                    name="managementTrafficfines.date"
                    type="date"
                    required={true}
                  />
                </div>
              )}
            </div>
          </div>

          <section className="bg-gray2/20 mt-6 mb-10 p-3 rounded-md shadow">
            <h2 className="text-2xl font-semibold mb-4">Endereço</h2>
            <AddressFields />
          </section>
        </section>
        <div className="w-full flex justify-end">
          <ButtonWithLoader
            isLoading={formik.isSubmitting}
            disabled={!formik.isValid}
            onClick={formik.handleSubmit}
          >
            Salvar
          </ButtonWithLoader>
        </div>
      </FormikProvider>
    </form>
  );
}

export default FormEditPartnersData;
